import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './JoinOrg.css';
import confetti from 'canvas-confetti';
import LoadingOverlay from '../components/LoadingOverlay';
import { portalLoginUrl } from '../utils/portalLoginUrl';

export default function JoinOrganizationPage() {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('inviteToken');
  // Invite mode: the user was invited and already has an account. The invite
  // token stands in for the company code, the org + email come from the invite,
  // and we only ask for the password.
  const inviteMode = !!inviteToken;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    orgCode: '',
  });
  const [orgName, setOrgName] = useState('');
  const [isCaddieGames, setIsCaddieGames] = useState(false);
  const [isMapleCoin, setIsMapleCoin] = useState(false);
  const [error, setError] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // In invite mode, resolve the org name + invited email from the invite token
  // (the token already encodes the target org — no company code needed).
  useEffect(() => {
    if (!inviteToken) return;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/register/validate-invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: inviteToken }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setError(data?.message || 'This invite is invalid or has expired.');
          setShowTooltip(true);
          return;
        }
        setFormData((prev) => ({ ...prev, email: data.email || '' }));
        setOrgName(data.platformName || data.legalCompanyName || 'this organization');
        setIsCaddieGames(data.isCaddieGames === true);
        setIsMapleCoin(data.isMapleCoin === true);
      } catch {
        setError('Could not load the invitation. Please try again.');
        setShowTooltip(true);
      }
    })();
  }, [inviteToken]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setShowTooltip(false);

    // Basic client-side validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address.');
      setShowTooltip(true);
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      setShowTooltip(true);
      return;
    }
    // Company code is only required for the manual join path; in invite mode the
    // token supplies the org.
    if (!inviteMode && !formData.orgCode.trim()) {
      setError('Organization code is required.');
      setShowTooltip(true);
      return;
    }

    setLoading(true);
    try {
      const body = inviteMode
        ? { email: formData.email, password: formData.password, inviteToken }
        : formData;
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/register/user/existing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        setLoading(false);
        setTimeout(() => {
          confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
          setTimeout(() => {
            // Portal invitees (CaddieGames, MapleCoin) land on their brand's
            // per-env login URL, which drives that brand's login flow.
            window.location.href = portalLoginUrl({ isCaddieGames, isMapleCoin });
          }, 2500);
        }, 100);
      } else {
        const responseData = await res.json();
        setError(responseData?.message || 'Something went wrong');
        setShowTooltip(true);
        setLoading(false);
      }
    } catch (err: any) {
      setError(err?.message || 'Network error');
      setShowTooltip(true);
      setLoading(false);
    }
  };

  return (
    <div className="registration-page-container">
      <LoadingOverlay show={loading} message="Joining organization..." />
      <form className="join-org-form" onSubmit={handleSubmit} autoComplete="off">
        {inviteMode ? (
          <>
            <h2>Join Organization</h2>
            <p style={{ margin: '0 0 4px', opacity: 0.8, fontSize: '0.9rem' }}>
              You have been invited to join:
            </p>
            <p style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 600 }}>
              {orgName || '…'}
            </p>
          </>
        ) : (
          <h2>Join Another Organization</h2>
        )}

        <input
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          required
          readOnly={inviteMode}
        />
        <div className="password-field-wrapper">
          <input
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            required
          />
          <button
            type="button"
            className="toggle-visibility"
            onClick={() => setShowPassword(!showPassword)}
            aria-label="Toggle password visibility"
          >
            <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {showPassword ? (
                  <>
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                    <circle cx="12" cy="12" r="3" />
                  </>
                ) : (
                  <>
                    <path d="M1 1l22 22" />
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                    <circle cx="12" cy="12" r="3" />
                  </>
                )}
              </svg>
          </button>
        </div>
        {!inviteMode && (
          <input
            type="text"
            name="orgCode"
            placeholder="Organization Code"
            value={formData.orgCode}
            onChange={handleChange}
            required
          />
        )}

        <button className="submit-btn" type="submit" style={{ border: '1px solid white' }}>
          {inviteMode ? 'Join Organization' : 'Confirm'}
        </button>
        {showTooltip && (
          <div style={{marginTop:'4px',}} className='tooltip tooltip-error'>
            {error}
          </div>
        )}
      </form>
    </div>
  );
}
